import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';

export enum ToastSeverity {
  SUCCESS = 'success',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

export type ToastPosition =
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left';

export interface ToastMessage {
  id: number;
  severity: ToastSeverity;
  summary: string;
  detail?: string;
  life?: number;
  closable?: boolean;
  position?: ToastPosition;
}

export interface ToastOptions {
  life?: number;
  position?: ToastPosition;
  closable?: boolean;
}

interface ToastState {
  toasts: ToastMessage[];
}

const initialState: ToastState = {
  toasts: [],
};

const DEFAULT_LIFE = 3000;

export const ToastService = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store) => {
    let lastId = 0;

    const remove = (id: number): void => {
      patchState(store, {
        toasts: store.toasts().filter((toast) => toast.id !== id),
      });
    };

    const add = (toast: Omit<ToastMessage, 'id'>): void => {
      const id = ++lastId;
      const life = toast.life || DEFAULT_LIFE;
      const closable = toast.closable !== undefined ? toast.closable : true;

      const newToast: ToastMessage = { ...toast, id, life, closable };
      patchState(store, { toasts: [...store.toasts(), newToast] });

      if (life > 0) {
        setTimeout(() => remove(id), life);
      }
    };

    return {
      remove,
      clear(): void {
        patchState(store, { toasts: [] });
      },
      success(summary: string, detail?: string, options?: ToastOptions): void {
        add({
          severity: ToastSeverity.SUCCESS,
          summary,
          detail,
          life: options?.life,
          position: options?.position,
          closable: options?.closable,
        });
      },
      info(summary: string, detail?: string, options?: ToastOptions): void {
        add({
          severity: ToastSeverity.INFO,
          summary,
          detail,
          life: options?.life,
          position: options?.position,
          closable: options?.closable,
        });
      },
      warn(summary: string, detail?: string, options?: ToastOptions): void {
        add({
          severity: ToastSeverity.WARN,
          summary,
          detail,
          life: options?.life,
          position: options?.position,
          closable: options?.closable,
        });
      },
      error(summary: string, detail?: string, options?: ToastOptions): void {
        add({
          severity: ToastSeverity.ERROR,
          summary,
          detail,
          life: options?.life,
          position: options?.position,
          closable: options?.closable,
        });
      },
    };
  })
);
